import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Phone, Scissors, Stethoscope } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "@/state/AppContext";
import { useRequireAuth } from "@/lib/useRequireAuth";
import { api, apiErrorMessage, rows } from "@/lib/api";
import { localized } from "@/theme/tenantTheme";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { rupees, localTime, businessDay } from "@/lib/format";
import { scrollToSection } from "@/storefront/scroll";
import { trackInteraction } from "@/storefront/trackInteraction";
import { telHref, validatePhone } from "@/lib/phone";

const today = () => businessDay();

// F4/L2-005: `appointments` is shared by `clinic` and `salon` (and other appointments-capable
// presets, e.g. `ac_dealer`) — a preset not in this map falls back to clinic copy/icon, the
// wording the component always had.
const VERTICAL_ICONS = { salon: Scissors };
function verticalKeyFor(business) {
  // D3 (storefront S3): was `business.preset`, a field that only ever exists on an
  // `OnboardingRequest` row (`directory/models.py`) — `BusinessOut`/`StorefrontOut` never carry a
  // `preset` key, only `business_type` (where a preset's slug actually lands once applied, per
  // `directory/presets.py`'s own doc comment). The check was therefore always false, so every
  // `appointments`-capable business — including every salon — rendered clinic copy. Fixed to read
  // the field the wire shape actually has.
  return business.business_type === "salon" ? "salon" : "clinic";
}

/** PR4 (`design_handoff_business_websites`): the "Doctor on call" sub-feature, gated on
 * `storefront_config.vertical.onCall === true` (the `appointments` capability is already this
 * component's own precondition — `sectionsFor` never mounts it otherwise). Additive: it renders
 * alongside the existing per-doctor slot booking below, never instead of it.
 *
 * Home visit degrades through what the business actually has, same pattern as `PlansBlock`'s
 * CTAs: `service_visit` section if present, else `enquiry`, else `tel:`.
 *
 * **No ambulance option.** The brief asked to reuse "the ambulance capability/feature" a
 * business already exposes — there isn't one: `core/enums.CAPABILITIES` has no `ambulance`
 * member, and the platform's only ambulance flow (`pages/Ambulance.jsx`, `/ambulance`) is a
 * city-wide dispatch request with no business/clinic scoping at all, not a per-clinic feature. A
 * business can never satisfy "has the ambulance capability" because that capability does not
 * exist, so per the brief's own fallback ("if it doesn't, omit the ambulance option — do not
 * build a new ambulance flow") this option is omitted entirely rather than linked to a page that
 * isn't actually this clinic's. */
function DoctorOnCall({ business }) {
  const { t } = useApp();
  if (business.storefront_config?.vertical?.onCall !== true) return null;
  const caps = business.capabilities || [];

  const homeVisit = () => {
    if (caps.includes("service_visit")) return scrollToSection("service_visit");
    if (caps.includes("enquiry")) return scrollToSection("enquiry");
    const href = telHref(business.phone);
    if (!href) return;
    trackInteraction(business.id, "call");
    window.location.href = href;
  };

  return (
    <div className="space-y-3 rounded-2xl border border-border p-5" data-testid="doctor-on-call">
      <p className="font-heading text-base font-semibold">{t("appointments.onCallTitle")}</p>
      <div className="flex flex-wrap gap-3">
        {telHref(business.phone) && (
          <Button asChild variant="outline" className="rounded-full" data-testid="doctor-on-call-phone" onClick={() => trackInteraction(business.id, "call")}>
            <a href={telHref(business.phone)}>
              <Phone size={15} className="mr-2" /> {t("appointments.onCallPhone")}
            </a>
          </Button>
        )}
        <Button variant="outline" className="rounded-full" data-testid="doctor-on-call-home-visit" onClick={homeVisit}>
          <Home size={15} className="mr-2" /> {t("appointments.onCallHomeVisit")}
        </Button>
      </div>
    </div>
  );
}

export function DoctorSection({ business }) {
  const { t, lang, auth } = useApp();
  const navigate = useNavigate();
  const requireAuth = useRequireAuth();
  const [doctors, setDoctors] = useState([]);
  const [selected, setSelected] = useState(null);
  const [date, setDate] = useState(today());
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState("");
  const [form, setForm] = useState({ patient_name: "", patient_phone: "", notes: "", payment_method: "cash" });
  const [busy, setBusy] = useState(false);

  const vertical = verticalKeyFor(business);
  const ProviderIcon = VERTICAL_ICONS[vertical] || Stethoscope;
  const tv = (key) => t(`appointments.vertical.${vertical}.${key}`);

  // F4/L2-005: home-service toggle, an owner-configured sub-feature of `appointments`
  // (`storefront_config.vertical.homeService`, offered to salon/beauty presets in Website
  // Settings). "At home" needs an address, which — per the brief — rides in the SAME `notes`
  // field the booking already posts (no new backend field/column).
  const homeServiceEnabled = business.storefront_config?.vertical?.homeService === true;
  const [serviceMode, setServiceMode] = useState("salon"); // "salon" | "home"
  const [homeAddress, setHomeAddress] = useState("");

  useEffect(() => {
    api.get(`/businesses/${business.id}/doctors`).then((r) => {
      setDoctors(rows(r.data));
      if (r.data[0]) setSelected(r.data[0]);
    });
  }, [business.id]);

  useEffect(() => {
    if (!selected) return;
    setSlot("");
    api.get(`/doctors/${selected.id}/slots`, { params: { day: date } }).then((r) => setSlots(r.data.slots));
  }, [selected, date]);

  const book = async () => {
    // One up-front convention (L2): the shared sign-in prompt, which returns here after login.
    if (!requireAuth()) return;
    // R11 PHONE: the same rule the server applies, in the visitor's language, before the round trip.
    const phoneCheck = validatePhone(form.patient_phone, "mobile");
    if (!phoneCheck.ok) {
      toast.error(t(phoneCheck.messageKey));
      return;
    }
    if (homeServiceEnabled && serviceMode === "home" && !homeAddress.trim()) {
      toast.error(t("appointments.homeService.addressRequired"));
      return;
    }
    setBusy(true);
    try {
      const notes =
        homeServiceEnabled && serviceMode === "home"
          ? [`${t("appointments.homeService.atHome")}: ${homeAddress.trim()}`, form.notes.trim()]
              .filter(Boolean)
              .join(" — ")
          : form.notes;
      await api.post("/appointments", { doctor_id: selected.id, slot_start_at: slot, ...form, notes });
      if (form.payment_method === "upi") {
        navigate("/activity");
        return;
      }
      toast.success(t("appointments.booked"));
      setSlot("");
      const { data } = await api.get(`/doctors/${selected.id}/slots`, { params: { day: date } });
      setSlots(data.slots);
    } catch (e) {
      toast.error(apiErrorMessage(e, t("errors.generic")));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="doctor-section">
      <div className="grid gap-3 sm:grid-cols-2">
        {doctors.map((d) => (
          <button
            key={d.id}
            onClick={() => setSelected(d)}
            data-testid={`doctor-card-${d.id}`}
            className={`flex items-start gap-3 rounded-2xl border p-4 text-left transition-colors duration-200 ${
              selected?.id === d.id ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-border"
            }`}
          >
            <span className="grid h-10 w-10 place-items-center rounded-xl bg-muted text-[hsl(var(--primary))]">
              <ProviderIcon size={17} />
            </span>
            <span>
              <span className="block font-medium">{d.name}</span>
              <span className="block text-sm text-[hsl(var(--primary))]">{localized(lang, d, "specialty")}</span>
              <span className="block text-xs text-muted-foreground">{d.qualifications}</span>
              <span className="mt-1 block tabular-nums text-xs">
                {t("appointments.fee")}: {rupees({ minor_units: d.fee?.minor_units })}
              </span>
            </span>
          </button>
        ))}
        {doctors.length === 0 && <p className="text-muted-foreground">{t("common.noResults")}</p>}
      </div>

      <DoctorOnCall business={business} />

      {selected && (
        <div className="space-y-4 rounded-2xl border border-border p-5">
          <div className="space-y-1.5">
            <Label htmlFor="appt-date">{t("appointments.selectDate")}</Label>
            <Input
              id="appt-date"
              type="date"
              min={today()}
              value={date}
              data-testid="appointment-date-input"
              onChange={(e) => setDate(e.target.value)}
              className="max-w-xs"
            />
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {t("appointments.selectSlot")}
            </p>
            {slots.filter((s) => s.available).length === 0 ? (
              <p className="mt-2 text-sm text-muted-foreground" data-testid="no-slots">
                {t("appointments.noSlots")}
              </p>
            ) : (
              <div className="mt-3 flex flex-wrap gap-2" data-testid="slot-list">
                {slots.map((s) => (
                  <button
                    key={s.start_at}
                    disabled={!s.available}
                    onClick={() => setSlot(s.start_at)}
                    data-testid={`slot-${s.start_at}`}
                    className={`rounded-full border px-3 py-1.5 tabular-nums text-xs transition-colors duration-200 ${
                      slot === s.start_at
                        ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                        : s.available
                          ? "border-border"
                          : "border-dashed border-border text-muted-foreground/50"
                    }`}
                  >
                    {localTime(s.start_at, lang)}
                  </button>
                ))}
              </div>
            )}
          </div>

          {slot && (
            <div className="space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="patient-name">{tv("patientName")}</Label>
                  <Input
                    id="patient-name"
                    data-testid="patient-name-input"
                    value={form.patient_name}
                    onChange={(e) => setForm({ ...form, patient_name: e.target.value })}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="patient-phone">{tv("patientPhone")}</Label>
                  <Input
                    id="patient-phone"
                    data-testid="patient-phone-input"
                    value={form.patient_phone}
                    onChange={(e) => setForm({ ...form, patient_phone: e.target.value })}
                  />
                </div>
              </div>
              {homeServiceEnabled && (
                <div className="space-y-2" data-testid="home-service-toggle">
                  <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                    {t("appointments.homeService.modeLabel")}
                  </p>
                  <div className="flex gap-2">
                    {[
                      ["salon", t("appointments.homeService.atSalon")],
                      ["home", t("appointments.homeService.atHome")],
                    ].map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        data-testid={`home-service-mode-${value}`}
                        onClick={() => setServiceMode(value)}
                        className={`rounded-full border px-4 py-1.5 text-sm ${
                          serviceMode === value
                            ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                            : "border-border"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {serviceMode === "home" && (
                    <div className="space-y-1.5">
                      <Label htmlFor="home-service-address">{t("appointments.homeService.addressLabel")}</Label>
                      <Input
                        id="home-service-address"
                        data-testid="home-service-address-input"
                        value={homeAddress}
                        onChange={(e) => setHomeAddress(e.target.value)}
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="patient-notes">{t("appointments.notes")}</Label>
                <Input
                  id="patient-notes"
                  data-testid="appointment-notes-input"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                />
              </div>
              {/* UAT fix: `business` here is the public storefront shape, which never sends
                  `upi_id` (owner-only) — `accepts_upi` is the public boolean that carries this. */}
              {business.accepts_upi && (
                <div className="flex gap-2">
                  {[
                    ["cash", t("payment.cashMode")],
                    ["upi", t("payment.upiMode")],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      data-testid={`appointment-payment-method-${value}`}
                      onClick={() => setForm({ ...form, payment_method: value })}
                      className={`rounded-full border px-4 py-1.5 text-sm ${
                        form.payment_method === value
                          ? "border-transparent bg-[hsl(var(--primary))] text-primary-foreground"
                          : "border-border"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
              <Button className="rounded-full" data-testid="book-appointment-btn" disabled={busy} onClick={book}>
                {busy ? t("common.saving") : auth ? tv("book") : tv("signInToBook")}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
